import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './useAuth';
import { mergeWells, resolveAccess } from '../lib/access';
import type { Access, AdminStore, AdminWell } from '../lib/access';
import { ADMIN_SIMULATED, ADMIN_STORAGE_KEY, loadAdminStore, readAdminStore } from '../lib/adminApi';
import { ApiError } from '../lib/api';

/** Which user a superadmin is looking at the portal as. Ends with the tab. */
const PREVIEW_KEY = 'wxp.previewAs';

interface AdminValue {
  /** Companies, roles, layouts and users, as last read from where they are kept. */
  store: AdminStore;
  /** Runs one admin call and takes its result as the new state. Rejects as the call does. */
  commit: (call: Promise<AdminStore>) => Promise<AdminStore>;
  /** False until the admin area has been read once for whoever is signed in. */
  storeLoaded: boolean;

  /** Every well this person is served, each with its company. */
  allWells: AdminWell[];
  /** The wells this person may open. */
  wells: AdminWell[];
  /** False until the API has answered once; the lists above are partial before that. */
  wellsLoaded: boolean;
  wellsError: string | null;

  /** What the person in front of the screen may do. */
  access: Access;
  previewAs: (userId: number | null) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AdminContext = createContext<AdminValue | null>(null);

function readPreview(): number | null {
  try {
    const raw = sessionStorage.getItem(PREVIEW_KEY);
    return raw ? Number(raw) || null : null;
  } catch {
    return null;
  }
}

/** A save in mock mode answers without the wells; they are the ones already read. */
const keepingWells = (next: AdminStore, have: AdminStore): AdminStore =>
  next.serverWells ? next : { ...next, serverWells: have.serverWells };

export function AdminProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const [store, setStore] = useState<AdminStore>(readAdminStore);
  const [preview, setPreview] = useState<number | null>(readPreview);
  /** Who the store was last read for, and what went wrong if it could not be. */
  const [loaded, setLoaded] = useState<{ for: string; error: string | null } | null>(null);

  // Saved in another tab: follow it, so two open tabs never disagree. Only the
  // simulation is kept where a tab can see another's change.
  useEffect(() => {
    if (!ADMIN_SIMULATED) return;
    function onStorage(e: StorageEvent) {
      if (e.key === ADMIN_STORAGE_KEY || e.key === null) setStore((have) => keepingWells(readAdminStore(), have));
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // The admin area and the wells are read once per signed-in account, here,
  // rather than by every page that shows them. What one account was shown is
  // dropped the moment another signs in.
  const who = user ? String(user.id) : null;
  useEffect(() => {
    if (!user || !who) return;
    let cancelled = false;

    setStore(readAdminStore());
    loadAdminStore(user)
      .then((next) => {
        if (cancelled) return;
        setStore(next);
        setLoaded({ for: who, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const error = err instanceof ApiError ? err.message : 'Could not load the wells.';
        setLoaded({ for: who, error });
      });

    return () => {
      cancelled = true;
    };
    // Only who is signed in decides a reload: the same account restored from
    // its session is a new object and the same person.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who]);

  const commit = useCallback(async (call: Promise<AdminStore>) => {
    const next = await call;
    setStore((have) => keepingWells(next, have));
    return next;
  }, []);

  const previewAs = useCallback((userId: number | null) => {
    try {
      if (userId == null) sessionStorage.removeItem(PREVIEW_KEY);
      else sessionStorage.setItem(PREVIEW_KEY, String(userId));
    } catch {
      /* storage unavailable — the preview lasts until the page reloads */
    }
    setPreview(userId);
  }, []);

  const value = useMemo<AdminValue>(() => {
    const mine = loaded && loaded.for === who ? loaded : null;
    const access = resolveAccess(user, store, preview);
    const allWells = mergeWells(store.serverWells ?? [], store);

    return {
      store,
      commit,
      storeLoaded: mine !== null,
      allWells,
      wells: allWells.filter(access.seesWell),
      wellsLoaded: mine !== null,
      wellsError: mine?.error ?? null,
      access,
      previewAs,
    };
  }, [user, who, store, preview, loaded, commit, previewAs]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}
